import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  CreditCard, 
  Target,
  RefreshCw
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import MetricCard from '../components/MetricCard';
import ComprehensiveChart from '../components/ComprehensiveChart';
import SegmentHeatmap from '../components/SegmentHeatmap';
import dataService from '../services/dataService';
import analyticsService from '../services/analyticsService';

const Dashboard = () => {
  usePageTitle('Trading Dashboard');
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['FO', 'EQ', 'MF', 'CDS', 'COM']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await dataService.loadData();
      const summaryData = dataService.getSummaryData();
      setData(summaryData);
      setSelectedYears(summaryData.map(d => d.fy)); // Select all years by default
      
      // Track dashboard data load
      analyticsService.trackDashboardInteraction('data_load', 'dashboard', {
        years_count: summaryData.length,
        total_pnl: summaryData.reduce((sum, d) => sum + d.total_pnl, 0)
      });
    } catch (error) {
      console.error('Error loading data:', error);
      analyticsService.trackError('Dashboard data load failed', 'dashboard', 'data_load');
    } finally {
      setLoading(false);
    }
  };

  // Track filter changes
  const handleYearFilter = (year, isChecked) => {
    if (isChecked) {
      setSelectedYears([...selectedYears, year]);
    } else {
      setSelectedYears(selectedYears.filter(y => y !== year));
    }
    
    analyticsService.trackFilter('year', year, 'dashboard');
    analyticsService.trackDashboardInteraction('filter_change', 'year_filter', {
      year: year,
      action: isChecked ? 'add' : 'remove'
    });
  };

  const handleSegmentFilter = (segment, isChecked) => {
    if (isChecked) {
      setSelectedSegments([...selectedSegments, segment]);
    } else {
      setSelectedSegments(selectedSegments.filter(s => s !== segment));
    }
    
    analyticsService.trackFilter('segment', segment, 'dashboard');
    analyticsService.trackDashboardInteraction('filter_change', 'segment_filter', {
      segment: segment,
      action: isChecked ? 'add' : 'remove'
    });
  };

  // Track refresh button
  const handleRefreshData = () => {
    analyticsService.trackButtonClick('Refresh Data', 'dashboard', {
      component: 'refresh_button'
    });
    loadData();
  };

  // Track metric card interactions
  const handleMetricCardView = (metricName, metricValue) => {
    analyticsService.trackMetricView(metricName, metricValue);
    analyticsService.trackDashboardInteraction('metric_view', 'metric_card', {
      metric_name: metricName,
      metric_value: metricValue
    });
  };

  const filteredData = data.filter(d => selectedYears.includes(d.fy));

  // Calculate summary metrics
  const totalPnL = filteredData.reduce((sum, d) => sum + d.total_pnl, 0);
  const totalCharges = filteredData.reduce((sum, d) => sum + d.total_charges, 0);
  const netPnL = filteredData.reduce((sum, d) => sum + d.net_pnl, 0);
  const profitYears = filteredData.filter(d => d.net_pnl > 0).length;
  const winRate = filteredData.length > 0 ? (profitYears / filteredData.length) * 100 : 0;

  const bestYear = filteredData.length > 0 ? 
    filteredData.reduce((best, current) => current.net_pnl > best.net_pnl ? current : best) : null;

  const avgPnL = filteredData.length > 0 ? netPnL / filteredData.length : 0;

  // Get shared metrics (CAGR and best year from all data)
  const sharedMetrics = dataService.getSharedMetrics();
  const cagr = sharedMetrics ? sharedMetrics.cagr : 0;
  const lastUpdated = dataService.getDaysAgo();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
                      <RefreshCw className="w-12 h-12 text-gray-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading trading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Trading Performance Dashboard
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive analysis of my trading performance across all segments and financial years.
              This dashboard shows my actual annual P&L on Zerodha.
            </p>
            {lastUpdated && (
              <p className="text-sm text-gray-500 mt-4">
                Last updated: {lastUpdated}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Portfolio Overview Hero Section */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-3xl font-bold mb-2 text-gray-900">Portfolio Performance Overview</h2>
            <p className="text-gray-600 mb-8">
              Analysis across {filteredData.length} financial years • Annual P&L Impact Methodology
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div 
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => handleMetricCardView('Total Net P&L', netPnL)}
              >
                <div className="text-gray-600 text-sm font-medium mb-2">Total Net P&L</div>
                <div className={`text-3xl font-bold ${netPnL >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {dataService.formatCurrency(netPnL)}
                </div>
              </div>
              
              <div 
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => handleMetricCardView('Average per Year', avgPnL)}
              >
                <div className="text-gray-600 text-sm font-medium mb-2">Average per Year</div>
                <div className="text-3xl font-bold text-gray-900">
                  {dataService.formatCurrency(avgPnL)}
                </div>
              </div>
              
              <div 
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => handleMetricCardView('CAGR', cagr)}
              >
                <div className="text-gray-600 text-sm font-medium mb-2">CAGR (Excl. MF)</div>
                <div className="text-3xl font-bold text-gray-900">
                  {cagr.toFixed(1)}%
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  on ₹30L capital
                </div>
              </div>
              
              <div 
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => handleMetricCardView('Best Year', bestYear?.net_pnl || 0)}
              >
                <div className="text-gray-600 text-sm font-medium mb-2">Best Year</div>
                <div className="text-xl font-bold text-gray-900">
                  {bestYear?.fy || 'N/A'}
                </div>
                <div className="text-gray-600 text-sm">
                  {bestYear ? dataService.formatCurrency(bestYear.net_pnl) : 'N/A'} on ₹30L capital
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Gross P&L"
            value={totalPnL}
            icon={TrendingUp}
            type={totalPnL >= 0 ? "profit" : "loss"}
          />
          <MetricCard
            title="Total Charges"
            value={totalCharges}
            icon={CreditCard}
            type="loss"
          />
          <MetricCard
            title="Best Year"
            value={bestYear ? bestYear.net_pnl : 0}
            icon={DollarSign}
            type={bestYear && bestYear.net_pnl >= 0 ? "profit" : "loss"}
          />
          <MetricCard
            title="Win Rate"
            value={winRate}
            icon={Target}
            type="primary"
            isPercentage={true}
          />
        </div>

        {/* Comprehensive Chart - Full Width */}
        <div className="mb-8">
          <ComprehensiveChart data={filteredData} height={500} />
        </div>

        {/* Segment Heatmap - Full Width */}
        <div className="mb-8">
          <SegmentHeatmap data={filteredData} />
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Filters</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Year Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Financial Years
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {data.map(d => (
                  <label key={d.fy} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedYears.includes(d.fy)}
                      onChange={(e) => handleYearFilter(d.fy, e.target.checked)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{d.fy}</span>
                    <span className={`ml-auto text-xs font-medium ${
                      d.net_pnl >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {dataService.formatCurrency(d.net_pnl)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Segment Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Trading Segments
              </label>
              <div className="space-y-2">
                {dataService.getSegments().map(segment => {
                  const segmentInfo = dataService.getSegmentInfo();
                  return (
                    <label key={segment} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedSegments.includes(segment)}
                        onChange={(e) => handleSegmentFilter(segment, e.target.checked)}
                        className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {segment} - {segmentInfo[segment]}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              onClick={handleRefreshData}
              className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200"
            >
              <RefreshCw className="mr-2 w-5 h-5" />
              Refresh Data
            </button>
          </div>
        </div>

        {/* Methodology Note */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Methodology</h4>
          <p className="text-gray-700">
            📊 <strong>Annual P&L Analysis:</strong> This dashboard shows the actual P&L impact for each financial year. 
            For unrealized positions, we calculate year-over-year changes (not cumulative totals), 
            which represents the true performance attribution for each year.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;